import { EProfileType } from '../entities/IGlobal';
import { IInquiryPartiesStakeWire, IWizardStakeProfile } from '../entities/IInquiryTableWizard';
import {
    IPrincipalColumn,
    PrincipalColumnPrincipalTypeNumeric,
    PrincipalColumnPrincipalTypeWire
} from '../entities/IPrincipalColumn';
import { principalColumnToDisplay } from './principalDisplayMappers';
import { isGroupPrincipal, normalizePrincipalType } from './principalType';

const normalizeCabinetValue = (cabinet: unknown): string => {
    if (cabinet == undefined) {
        return '';
    }
    if (typeof cabinet === 'string') {
        return cabinet;
    }
    if (typeof cabinet === 'object' && cabinet !== null && 'name' in cabinet) {
        return String((cabinet as { name?: string }).name ?? '');
    }
    return '';
};

type PickerPrincipalData = {
    id?: string;
    type?: string | number | EProfileType;
    /** Same source as permissions search `type` (forwarded to clusters inquiry body). */
    apiPrincipalType?: string | number | null;
    firstName?: string;
    lastName?: string;
    /** Full display label from search (e.g. platform contacts API sends `name` only). */
    name?: string;
    fullName?: string;
    picture?: string;
    jobTitle?: string;
    emailContact?: string;
    cabinet?: string;
    groups?: string[];
    organizationsGroups?: string[];
};

function profileTypeToPrincipalColumnNumeric(
    type: EProfileType | string | undefined
): PrincipalColumnPrincipalTypeNumeric {
    if (type === EProfileType.LegalEntity || type === 'legalentity') {
        return 2;
    }
    if (isGroupPrincipal(type)) {
        return 1;
    }
    return 0;
}

/**
 * Pass through `principalType` exactly as from clusters/permissions APIs (`apiPrincipalType`), else UI `type` (e.g. EProfileType), else numeric fallback.
 */
function wirePrincipalTypeForInquiryPayload(profile: IWizardStakeProfile): PrincipalColumnPrincipalTypeWire {
    const raw = profile.apiPrincipalType;
    if (raw !== undefined && raw !== null && raw !== '') {
        return raw;
    }
    const t = profile.type;
    if (t !== undefined && t !== null && String(t) !== '') {
        return t;
    }
    return profileTypeToPrincipalColumnNumeric(t);
}

/**
 * Maps wizard picker state to the Clusters API wire shape for inquiry parties (childId + display lines + principalType).
 */
export function wizardStakeProfileToInquiryPartiesWire(
    profile: IWizardStakeProfile | undefined | null
): IInquiryPartiesStakeWire | undefined {
    const childId = profile?.id?.trim();
    if (!childId) {
        return undefined;
    }
    const isGroup = isGroupPrincipal(profile.type);
    const nameLine = (profile.name ?? '').trim();
    const first = (profile.firstName ?? '').trim();
    const last = (profile.lastName ?? '').trim();
    const firstLine = isGroup
        ? (nameLine || first)
        : [first, last].filter(Boolean).join(' ').trim() || nameLine;
    const pic = (profile.picture ?? '').trim();

    return {
        childId,
        firstLine: firstLine || undefined,
        secondLine: (profile.jobTitle ?? '').trim() || undefined,
        picture: pic ? pic : undefined,
        principalType: wirePrincipalTypeForInquiryPayload(profile)
    };
}

export function principalColumnToWizardStakeProfile(column: IPrincipalColumn): IWizardStakeProfile {
    const d = principalColumnToDisplay(column);
    const isGroup = isGroupPrincipal(d.principalType);
    return {
        id: d.childId,
        cabinet: '',
        emailContact: '',
        firstName: isGroup ? '' : d.firstLine,
        lastName: '',
        jobTitle: d.secondLine,
        picture: d.picture,
        type: d.principalType,
        apiPrincipalType: column.principalType ?? undefined,
        groups: [],
        organizationsGroups: [],
        ...(isGroup ? { name: d.firstLine } : {})
    };
}

/**
 * Converts inquiry parties from convert-inquiry-stakes API (wire `childId`) or keeps full wizard profiles (`id`).
 */
export function inquiryPartiesStakeToWizardStakeProfile(
    stake: IWizardStakeProfile | IInquiryPartiesStakeWire | null | undefined
): IWizardStakeProfile | undefined {
    if (!stake) {
        return undefined;
    }
    const wizard = stake as IWizardStakeProfile & Partial<IInquiryPartiesStakeWire>;
    const id =
        wizard.id != null && String(wizard.id).trim() !== '' ? String(wizard.id).trim() : '';
    if (id) {
        const w = wizard as IWizardStakeProfile;
        return {
            ...w,
            id,
            emailContact: w.emailContact ?? '',
            groups: Array.isArray(w.groups) ? w.groups : [],
            organizationsGroups: Array.isArray(w.organizationsGroups) ? w.organizationsGroups : []
        };
    }
    const wire = stake as IInquiryPartiesStakeWire;
    const childId =
        wire.childId != null && String(wire.childId).trim() !== ''
            ? String(wire.childId).trim()
            : '';
    if (!childId) {
        return undefined;
    }
    return principalColumnToWizardStakeProfile({
        childId,
        firstLine: wire.firstLine,
        secondLine: wire.secondLine,
        picture: wire.picture,
        principalType: wire.principalType
    });
}

export function normalizeInquiryWizardPartiesInformation(
    parties:
        | {
              localDistributor?: IWizardStakeProfile | IInquiryPartiesStakeWire;
              clientCoordinator?: IWizardStakeProfile | IInquiryPartiesStakeWire;
          }
        | undefined
): { localDistributor?: IWizardStakeProfile; clientCoordinator?: IWizardStakeProfile } | undefined {
    if (!parties) {
        return undefined;
    }
    const localDistributor = inquiryPartiesStakeToWizardStakeProfile(parties.localDistributor);
    const clientCoordinator = inquiryPartiesStakeToWizardStakeProfile(parties.clientCoordinator);
    return {
        ...parties,
        localDistributor: localDistributor ?? (parties.localDistributor as IWizardStakeProfile | undefined),
        clientCoordinator: clientCoordinator ?? (parties.clientCoordinator as IWizardStakeProfile | undefined)
    };
}

export function mapPickerDataToWizardStakeProfile(
    data: PickerPrincipalData | undefined,
    options?: { includeCabinet?: boolean }
): IWizardStakeProfile | undefined {
    if (!data?.id) {
        return undefined;
    }
    const principalType = normalizePrincipalType(data.type);
    const isGroup = isGroupPrincipal(principalType);
    const displayName = (data.name ?? data.fullName ?? '').trim();
    const first = (data.firstName ?? '').trim();
    const last = (data.lastName ?? '').trim();
    /** Personal principals from platform search often expose only a single `name`, not first/last. */
    const personalFirstName =
        !isGroup && !first && !last && displayName ? displayName : first;
    const apiPrincipalType =
        data.apiPrincipalType !== undefined && data.apiPrincipalType !== null
            ? data.apiPrincipalType
            : data.type;

    return {
        id: data.id,
        cabinet: options?.includeCabinet ? normalizeCabinetValue(data.cabinet) : '',
        emailContact: (data.emailContact ?? '').trim(),
        firstName: isGroup ? (displayName || first) : personalFirstName,
        lastName: isGroup ? '' : last,
        jobTitle: (data.jobTitle ?? '').trim(),
        picture: data.picture ?? '',
        type: principalType,
        apiPrincipalType,
        groups: Array.isArray(data.groups) ? data.groups : [],
        organizationsGroups: Array.isArray(data.organizationsGroups) ? data.organizationsGroups : [],
        ...(isGroup && displayName ? { name: displayName } : {})
    };
}
